import { X } from "lucide-react";

import { Dialog, DialogClose, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/Modal";
import BrandText from "@/components/shared/BrandText";
import StatusBadge from "@/components/shared/StatusBadge";
import { FEEDBACK_STATUS_COLOR, FEEDBACK_STATUS_LABEL, FEEDBACK_TYPE_LABEL } from "@/config/dropdownData";
import { FeedbackService } from "@/services/feedbackService";
import { formatDate } from "@/utils/dateUtils";

import { Screenshots } from "./Screenshot";

/**
 * One feedback in full, read-only, as the "My Feedback" table rows it: the
 * whole comment and its screenshots, its status, and the team's answer once it has ended. A
 * tenant admin's row also carries who sent it, and the view shows them.
 */
const FeedbackViewModal = ({ feedback, onClose }) => {
  const sentBy = "submitter_name" in feedback;
  // Top right, on whichever line comes first: who sent it for a tenant
  // admin, the comment's heading for everyone else.
  // None for Appreciation, which is never worked.
  const status = feedback.status ? (
    <StatusBadge text={FEEDBACK_STATUS_LABEL[feedback.status] ?? feedback.status} colorIndex={FEEDBACK_STATUS_COLOR[feedback.status] ?? 0} size="sm" />
  ) : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="w-[340px] md:w-[560px] flex flex-col">
        <DialogHeader className="relative pl-5 pr-14 py-3 border-b border-border-primary shrink-0">
          <DialogTitle className="text-[15px] font-semibold text-text-primary">{FEEDBACK_TYPE_LABEL[feedback.type] ?? feedback.type}</DialogTitle>
          <DialogDescription className="text-[12px] text-text-secondary">
            Sent {formatDate(feedback.created_at)}
            {feedback.is_imported && " · Imported"}
          </DialogDescription>
          <DialogClose aria-label="Close" className="absolute right-4 top-3 w-7 h-7 rounded-full bg-background-icon/40 flex items-center justify-center">
            <X size={16} strokeWidth={3} />
          </DialogClose>
        </DialogHeader>

        <div className="px-5 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
          {sentBy && (
            <div className="flex items-start justify-between gap-3">
              {feedback.submitter_name ? (
                <div className="flex flex-col">
                  <span className="text-[13px] text-text-primary">{feedback.submitter_name}</span>
                  {feedback.submitter_email && <span className="text-[12px] text-text-secondary">{feedback.submitter_email}</span>}
                </div>
              ) : (
                <span className="text-[12px] text-text-secondary">Removed user</span>
              )}
              {status}
            </div>
          )}

          <section aria-labelledby="my-feedback-comment-heading">
            <div className="mb-1 flex items-center justify-between gap-3">
              <h3 id="my-feedback-comment-heading" className="text-[12px] font-normal text-text-secondary">
                Comment
              </h3>
              {!sentBy && status}
            </div>
            <p className="whitespace-pre-wrap break-words text-sm text-text-primary">{feedback.comment}</p>
          </section>

          <Screenshots feedback={feedback} fetchImage={FeedbackService.fetchTenantImage} />

          {feedback.ended && feedback.end_reason && (
            <div className="border-t border-border-primary pt-4">
              <p className="mb-1 text-[12px] text-text-secondary">
                Response from the <BrandText /> team
              </p>
              <p className="whitespace-pre-wrap break-words text-sm text-text-primary">{feedback.end_reason}</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeedbackViewModal;
